import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useExcluirLancamento, useLancamentos, useSalvarLancamento, useTiposAtividade, type Lancamento, type TipoAtividade } from "../lib/queries";

const emptyForm = { tipo: "muro", data: "", valor: "", responsavel: "", titulo: "", subtitulo: "", cancelado: false };

export function LancarDados() {
  const { session, profile } = useAuth();
  const ano = new Date().getFullYear();
  const { data: tipos } = useTiposAtividade();
  const { data: entries, isLoading } = useLancamentos(ano);
  const salvar = useSalvarLancamento();
  const excluir = useExcluirLancamento();

  const [f, setF] = useState({ ...emptyForm, responsavel: profile?.nome ?? "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const tipoMap = useMemo(() => {
    const map = new Map<string, TipoAtividade>();
    tipos?.forEach((t) => map.set(t.slug, t));
    return map;
  }, [tipos]);

  function editEntry(entry: Lancamento) {
    setEditingId(entry.id);
    setF({
      tipo: entry.tipo,
      data: entry.data,
      valor: entry.cancelado ? "" : String(entry.valor ?? ""),
      responsavel: entry.responsavel || "",
      titulo: entry.titulo || "",
      subtitulo: entry.subtitulo || "",
      cancelado: entry.cancelado,
    });
    setSavedMsg("");
    setFormError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setF({ ...emptyForm, responsavel: profile?.nome ?? "" });
    setFormError("");
  }

  async function submitForm() {
    if (!f.data) {
      setFormError("Escolha uma data.");
      return;
    }
    if (!f.cancelado && f.valor === "") {
      setFormError("Informe o número de pessoas, ou marque como cancelado.");
      return;
    }
    setFormError("");
    setSavedMsg("");
    try {
      await salvar.mutateAsync({
        id: editingId ?? undefined,
        tipo: f.tipo,
        data: f.data,
        titulo: f.titulo,
        subtitulo: f.subtitulo,
        valor: f.cancelado ? null : Number(f.valor),
        cancelado: f.cancelado,
        observacao: "",
        responsavel: f.responsavel,
        criado_por: session?.user.id ?? null,
      });
      setSavedMsg("Salvo ✓");
      setF({ ...emptyForm, responsavel: profile?.nome ?? "" });
      setEditingId(null);
      setTimeout(() => setSavedMsg(""), 3000);
    } catch {
      setFormError("Falha ao salvar. Verifique sua conexão.");
    }
  }

  async function deleteEntry(id: string) {
    try {
      await excluir.mutateAsync(id);
    } catch {
      setFormError("Falha ao excluir.");
    }
  }

  const inputNumStyle = { background: f.cancelado ? "#F0EADB" : "#FFFCF4" };
  const submitting = salvar.isPending;

  const ordenados = [...(entries ?? [])].sort((a, b) => b.data.localeCompare(a.data));

  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(120% 80% at 85% -10%, #F6EFDF 0%, #EFE8DA 42%, #EAE1D0 100%)", padding: "34px clamp(20px,4vw,56px) 60px" }}>
      <div style={{ maxWidth: 920, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>
        {/* HEADER */}
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, borderBottom: "1.5px solid #DDD2BC", paddingBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: "#8E8672" }}>Sede CEF · Lançamentos</div>
            <h1 style={{ fontSize: "clamp(26px,3.6vw,36px)", letterSpacing: -1.2, lineHeight: 1, marginTop: 6 }}>Lançar Dados</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#4C6B4E" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#7C7462" }}>{profile?.nome}</span>
          </div>
        </header>

        {/* FORM CARD */}
        <section className="card" style={{ borderRadius: 22, padding: "26px 26px 22px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <h2 style={{ fontSize: 20, letterSpacing: -0.6 }}>{editingId ? "Editar lançamento" : "Novo lançamento"}</h2>
            {editingId && (
              <button onClick={cancelEdit} style={{ border: "none", background: "none", color: "#B24C4C", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancelar edição
              </button>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Tipo</span>
              <select className="input" value={f.tipo} onChange={(e) => setF((s) => ({ ...s, tipo: e.target.value }))}>
                {tipos?.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.emoji} {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Data</span>
              <input className="input" type="date" value={f.data} onChange={(e) => setF((s) => ({ ...s, data: e.target.value }))} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Nº de pessoas</span>
              <input
                className="input"
                type="number"
                min={0}
                style={inputNumStyle}
                placeholder="ex: 12"
                value={f.valor}
                disabled={f.cancelado}
                onChange={(e) => setF((s) => ({ ...s, valor: e.target.value }))}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Seu nome (responsável)</span>
              <input className="input" placeholder="Quem está lançando" value={f.responsavel} onChange={(e) => setF((s) => ({ ...s, responsavel: e.target.value }))} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Título (opcional)</span>
              <input className="input" placeholder="ex: CEF Cine · Meio Ambiente" value={f.titulo} onChange={(e) => setF((s) => ({ ...s, titulo: e.target.value }))} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#8E8672", textTransform: "uppercase", letterSpacing: 0.4 }}>Subtítulo / observação</span>
              <input className="input" placeholder="detalhes adicionais" value={f.subtitulo} onChange={(e) => setF((s) => ({ ...s, subtitulo: e.target.value }))} />
            </label>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={f.cancelado} onChange={(e) => setF((s) => ({ ...s, cancelado: e.target.checked }))} style={{ width: 18, height: 18, accentColor: "#B24C4C" }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: "#4A4A3E" }}>Marcar como cancelado / sem sessão</span>
          </label>

          {formError && (
            <div style={{ marginTop: 14, fontSize: 13, fontWeight: 600, color: "#B24C4C", background: "#F6E4E4", borderRadius: 10, padding: "9px 12px" }}>{formError}</div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20 }}>
            <button
              onClick={submitForm}
              disabled={submitting}
              style={{ border: "none", cursor: "pointer", fontWeight: 700, fontSize: 14, padding: "12px 24px", borderRadius: 11, background: submitting ? "#C4BBA4" : "#DB6B39", color: "#FBF8F1", boxShadow: "0 6px 16px -6px #DB6B3988" }}
            >
              {submitting ? "Salvando…" : editingId ? "Salvar alteração" : "Adicionar lançamento"}
            </button>
            {savedMsg && <span style={{ fontSize: 13, fontWeight: 600, color: "#4C6B4E" }}>{savedMsg}</span>}
          </div>
        </section>

        {/* LIST */}
        <section className="card" style={{ borderRadius: 22, padding: "20px 20px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, letterSpacing: -0.5 }}>Lançamentos ({ordenados.length})</h2>
            {isLoading && <span style={{ fontSize: 12.5, color: "#9A9280" }}>carregando…</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", maxHeight: 420, overflowY: "auto" }}>
            {ordenados.map((e) => {
              const meta = tipoMap.get(e.tipo);
              const [, m, d] = e.data.split("-");
              const isCanceled = e.cancelado;
              return (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 10px", borderRadius: 14, margin: "0 -6px", borderBottom: "1px solid #EFE8D6" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 44, flexShrink: 0 }}>
                    <span style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 16, lineHeight: 1 }}>{d}/{m}</span>
                  </div>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: meta?.cor ?? "#999", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#33372C" }}>{e.titulo || meta?.label}</div>
                    <div style={{ fontSize: 11.5, color: "#9A9280", fontWeight: 500 }}>
                      {meta?.label}{e.responsavel ? ` · lançado por ${e.responsavel}` : ""}
                    </div>
                  </div>
                  <span style={isCanceled
                    ? { fontSize: 11.5, fontWeight: 600, color: "#A79E88", background: "#F0EADB", padding: "5px 10px", borderRadius: 8, whiteSpace: "nowrap" }
                    : { fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 13, color: meta?.cor, background: meta?.cor_suave, padding: "5px 10px", borderRadius: 8, whiteSpace: "nowrap" }}>
                    {isCanceled ? "cancelado" : e.valor != null ? `${e.valor} pes.` : "—"}
                  </span>
                  <button onClick={() => editEntry(e)} style={{ border: "none", background: "#F0EADB", color: "#6B6350", fontSize: 12, fontWeight: 700, padding: "7px 11px", borderRadius: 8, cursor: "pointer" }}>
                    Editar
                  </button>
                  <button onClick={() => deleteEntry(e.id)} style={{ border: "none", background: "#F6E4E4", color: "#B24C4C", fontSize: 12, fontWeight: 700, padding: "7px 11px", borderRadius: 8, cursor: "pointer" }}>
                    Excluir
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <Link to="/" style={{ alignSelf: "center", fontSize: 13.5, fontWeight: 600, color: "#8E8672", textDecoration: "underline", marginTop: 4 }}>
          ← Voltar ao painel
        </Link>
      </div>
    </div>
  );
}
